/**
 * Jam Session Modal
 * Create or join a collaborative jam session
 */

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import {
  X,
  Music2,
  Users,
  Link2,
  ArrowRight,
  Loader2,
  Crown,
  Sparkles,
  Radio,
  Mic,
  Copy,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSubscription } from '../../../context/SubscriptionContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const JamSessionModal = ({
  isOpen,
  onClose,
  onSessionJoined,
  userId,
  userName,
  currentProjectId,
}) => {
  const [mode, setMode] = useState('select'); // select, create, join
  const [sessionName, setSessionName] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [maxParticipants, setMaxParticipants] = useState(4);
  const [loading, setLoading] = useState(false);
  const [previewSession, setPreviewSession] = useState(null);
  
  const { subscription, isPro, isStudio } = useSubscription();
  const canCreateSession = isPro || isStudio;

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMode('select');
      setSessionName('');
      setInviteCode('');
      setPreviewSession(null);
    }
  }, [isOpen]);

  const createSession = async () => {
    if (!sessionName.trim()) {
      toast.error('Please enter a session name');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/jam-sessions/create?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: sessionName,
          project_id: currentProjectId,
          max_participants: maxParticipants,
          is_private: true,
        }),
      });

      if (!response.ok) throw new Error('Failed to create session');
      
      const data = await response.json();
      toast.success('Jam session created!');
      onSessionJoined(data.session);
      onClose();
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Failed to create session');
    } finally {
      setLoading(false);
    }
  };

  const previewInviteCode = async () => {
    if (!inviteCode.trim() || inviteCode.length < 6) {
      toast.error('Please enter a valid invite code');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/jam-sessions/code/${inviteCode.toUpperCase()}`);
      
      if (!response.ok) {
        if (response.status === 404) {
          toast.error('Session not found - check your invite code');
        } else {
          throw new Error('Failed to find session');
        }
        return;
      }

      const data = await response.json();
      setPreviewSession(data);
    } catch (error) {
      console.error('Error previewing session:', error);
      toast.error('Failed to find session');
    } finally {
      setLoading(false);
    }
  };

  const joinSession = async () => {
    if (!previewSession) {
      await previewInviteCode();
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/jam-sessions/join?user_id=${userId}&user_name=${encodeURIComponent(userName)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          invite_code: inviteCode.toUpperCase(),
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        toast.error(error.detail || 'Failed to join session');
        return;
      }

      const data = await response.json();
      toast.success('Joined jam session!');
      onSessionJoined(data.session);
      onClose();
    } catch (error) {
      console.error('Error joining session:', error);
      toast.error('Failed to join session');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md bg-zinc-900 rounded-2xl border border-zinc-700 shadow-2xl overflow-hidden"
          data-testid="jam-session-modal"
        >
          {/* Header */}
          <div className="relative px-6 py-5 bg-gradient-to-r from-orange-500/20 via-amber-500/20 to-yellow-500/20 border-b border-zinc-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center">
                <Radio className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Jam Sessions</h2>
                <p className="text-sm text-zinc-400">Play together in real-time</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-2 text-zinc-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            {mode === 'select' && (
              <div className="space-y-4">
                {/* Create Session Option */}
                <button
                  onClick={() => canCreateSession ? setMode('create') : null}
                  disabled={!canCreateSession}
                  className={`w-full p-4 rounded-xl border text-left transition-all ${
                    canCreateSession
                      ? 'border-orange-500/30 bg-orange-500/10 hover:bg-orange-500/20 cursor-pointer'
                      : 'border-zinc-700 bg-zinc-800/50 cursor-not-allowed opacity-60'
                  }`}
                  data-testid="create-session-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      canCreateSession 
                        ? 'bg-orange-500' 
                        : 'bg-zinc-700'
                    }`}>
                      <Crown className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-white">Create Session</span>
                        {!canCreateSession && (
                          <span className="text-xs px-2 py-0.5 bg-orange-500/20 text-orange-400 rounded-full">
                            PRO
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-zinc-400">
                        {canCreateSession 
                          ? 'Start a new jam room and invite others'
                          : 'Upgrade to Pro to host jam sessions'
                        }
                      </p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-zinc-500" />
                  </div>
                </button>

                {/* Join Session Option */}
                <button
                  onClick={() => setMode('join')}
                  className="w-full p-4 rounded-xl border border-zinc-700 bg-zinc-800/50 hover:bg-zinc-800 text-left transition-all"
                  data-testid="join-session-btn"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                      <Users className="w-5 h-5 text-white" />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-white">Join Session</span>
                      <p className="text-sm text-zinc-400">Enter an invite code to join</p>
                    </div>
                    <ArrowRight className="w-5 h-5 text-zinc-500" />
                  </div>
                </button>

                {/* Info */}
                <div className="mt-6 p-4 bg-zinc-800/50 rounded-xl border border-zinc-700">
                  <div className="flex items-start gap-3">
                    <Sparkles className="w-5 h-5 text-amber-400 mt-0.5" />
                    <div>
                      <h4 className="font-medium text-white text-sm">Real-time Collaboration</h4>
                      <p className="text-xs text-zinc-400 mt-1">
                        Jam sessions let multiple musicians record and play together 
                        with synchronized playback and live audio streaming.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {mode === 'create' && (
              <div className="space-y-4">
                <button
                  onClick={() => setMode('select')}
                  className="text-sm text-zinc-400 hover:text-white flex items-center gap-1"
                >
                  ← Back
                </button>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Session Name
                  </label>
                  <Input
                    value={sessionName}
                    onChange={(e) => setSessionName(e.target.value)}
                    placeholder="Friday Night Jam"
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="session-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Max Participants
                  </label>
                  <div className="flex gap-2">
                    {[2, 4, 6, 8].map((num) => (
                      <button
                        key={num}
                        onClick={() => setMaxParticipants(num)}
                        className={`flex-1 py-2 rounded-lg border transition-all ${
                          maxParticipants === num
                            ? 'border-orange-500 bg-orange-500/20 text-orange-400'
                            : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'
                        }`}
                      >
                        {num}
                      </button>
                    ))}
                  </div>
                </div>

                <Button
                  onClick={createSession}
                  disabled={loading || !sessionName.trim()}
                  className="w-full bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white"
                  data-testid="create-session-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Radio className="w-4 h-4 mr-2" />
                  )}
                  Create Jam Session
                </Button>
              </div>
            )}

            {mode === 'join' && (
              <div className="space-y-4">
                <button
                  onClick={() => {
                    setMode('select');
                    setPreviewSession(null);
                    setInviteCode('');
                  }}
                  className="text-sm text-zinc-400 hover:text-white flex items-center gap-1"
                >
                  ← Back
                </button>

                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-2">
                    Invite Code
                  </label>
                  <Input
                    value={inviteCode}
                    onChange={(e) => {
                      setInviteCode(e.target.value.toUpperCase());
                      setPreviewSession(null);
                    }}
                    placeholder="ABC123"
                    maxLength={6}
                    className="bg-zinc-800 border-zinc-700 uppercase text-center text-xl tracking-widest font-mono"
                    data-testid="invite-code-input"
                  />
                </div>

                {/* Session Preview */}
                {previewSession && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-500 flex items-center justify-center">
                        <Music2 className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-white">{previewSession.name}</h4>
                        <p className="text-sm text-zinc-400">
                          Hosted by {previewSession.host_name} • {previewSession.participant_count}/{previewSession.max_participants} participants
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}

                <Button
                  onClick={joinSession}
                  disabled={loading || inviteCode.length < 6}
                  className="w-full bg-emerald-500 hover:bg-emerald-600 text-white"
                  data-testid="join-session-submit"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : previewSession ? (
                    <Users className="w-4 h-4 mr-2" />
                  ) : (
                    <Link2 className="w-4 h-4 mr-2" />
                  )}
                  {previewSession ? 'Join Session' : 'Find Session'}
                </Button>
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default JamSessionModal;
